class HighlightHelper {
    public static invalidWidth: number = 100000;

    /**
     * Generate all position and size of texts found within a container based on element's childNodes
     * @param {Element} container
     * @param {string} searchText
     * @param {boolean} [isCaseSensitive=false]
     * @returns {DOMRect[]}
     */
    public static getTextRectsToHighlightWithElement(container: Element, searchText: string, isCaseSensitive: boolean = false): DOMRect[] {
        const elementRects: DOMRect[] = [];
        const len = container.childNodes?.length ?? 0;

        for (let i = 0; i < len; i++) {
            const textToHighlight = container?.childNodes[i]?.textContent ?? "";
            let start: number = -1;

            do {
                if (isCaseSensitive) {
                    start = textToHighlight.indexOf(searchText, start + 1);
                }
                else {
                    start = textToHighlight.toLowerCase().indexOf(searchText.toLowerCase(), start + 1);
                }

                if (start !== -1) {
                    const end = start + searchText.length;
                    const range = document.createRange();

                    if (container?.childNodes[i]) {
                        range.setStart(container.childNodes[i], start);
                        range.setEnd(container.childNodes[i], end);

                        const textRects: DOMRectList = range.getClientRects();
                        if (textRects?.[0]) {
                            elementRects.push(textRects[0]);
                        }
                    }
                    start = end - 1;
                }
            } while (start !== -1);
        }

        return elementRects;
    }

    /**
     * Return all position and size of texts matched within a container based on textNode
     * @param {Element} nodeElement
     * @param {Text} nodeText
     * @param {string} searchText
     * @param {number} width
     * @param {boolean} [isCaseSensitive=false]
     * @returns {DOMRect[]}
     */
    public static getTextRectsToHighlightWithChildNode(nodeElement: Element, nodeText: Text, searchText: string, width: number, isCaseSensitive: boolean = false): DOMRect[] {
        const parentPosition: DOMRect = nodeElement?.getBoundingClientRect();
        const elementRects: DOMRect[] = [];
        const textToHighlight = nodeText.textContent ?? "";
        let start: number = -1;

        do {
            if (isCaseSensitive) {
                start = textToHighlight.indexOf(searchText, start + 1);
            }
            else {
                start = textToHighlight.toLowerCase().indexOf(searchText.toLowerCase(), start + 1);
            }

            if (start !== -1) {
                const end = start + searchText.length;
                const range = document.createRange();

                if (nodeText?.length && start <= nodeText.length && end <= nodeText.length) {
                    range.setStart(nodeText, start);
                    range.setEnd(nodeText, end);

                    const textRects = range.getClientRects();
                    if (this.isVisibleInParent(parentPosition, textRects?.[0], width)) {
                        elementRects.push(textRects[0]);
                    }
                }

                start = end - 1;
            }
        } while (start !== -1);

        return elementRects;
    }

    /**
     * Check if child is visible inside its parent
     * @param {DOMRect} parentPosition
     * @param {DOMRect} childPosition
     * @param {number} width
     * @returns {boolean}
     */
    public static isVisibleInParent(parentPosition: DOMRect, childPosition: DOMRect, width: number): boolean {
        if (!parentPosition || !childPosition) {
            return false;
        }
        else {
            const parentStartX = Math.floor(parentPosition.x);
            const parentEndX = Math.floor(parentPosition.x) + Math.floor(width);
            const parentStartY = Math.floor(parentPosition.y);
            const parentEndY = Math.floor(parentPosition.y) + Math.floor(parentPosition.height);
            const childStartX = Math.floor(childPosition.x);
            const childEndX = Math.floor(childPosition.x) + Math.floor(childPosition.width);
            const childStartY = Math.floor(childPosition.y);
            const childEndY = Math.floor(childPosition.y) + Math.floor(childPosition.height);

            return parentStartX <= childStartX && childEndX <= parentEndX && parentStartY <= childStartY && childEndY <= parentEndY;
        }
    }

    /**
     * Get width based on overflow in css
     * @param {HTMLElement} htmlElement
     * @param {boolean} isOverflowing
     * @param {number} currentWidth
     * @returns {number}
     */
    public static getWidth(htmlElement: HTMLElement, isOverflowing: boolean, currentWidth: number): number {
        let width = currentWidth;

        if (isOverflowing) {
            const elementWidth = IsNullOrWhiteSpace(htmlElement?.style?.width) ? this.invalidWidth : parseInt(htmlElement.style.width);
            const elementMaxWidth = IsNullOrWhiteSpace(htmlElement?.style?.maxWidth) ? this.invalidWidth : parseInt(htmlElement.style.maxWidth);

            width = Math.min(elementWidth, elementMaxWidth, currentWidth);
        }
        else {
            width = htmlElement?.getBoundingClientRect?.()?.width ?? this.invalidWidth;
        }

        return width;
    }

    /**
     * Return true if the given background color is transparent
     * @param {string} backgroundColor
     * @returns {boolean}
     */
    public static isBackgroundColorTransparent(backgroundColor: string): boolean {
        if (IsNullOrUndefined(backgroundColor)) {
            return false;
        }
        else {
            return backgroundColor === "rgba(0, 0, 0, 0)";
        }
    }
}

export { HighlightHelper };
